import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * 公開操作（アップロード時 / 公開設定切替時）の前に挟む確認モーダル。
 * 実装は DevPortal.tsx の PlanCompareModal を踏襲（Escape で閉じる・body スクロールロック等）。
 * 権利者確認・利用規約同意の2チェックボックスは、両方チェックするまで確定できない
 * （権利表明チェック・同意記録の要件。DevPortal.tsx の agree-box と同じ作法）。
 */
interface PublishConfirmModalProps {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  /** 主要ボタンのラベル（省略時「公開して投稿する」）。 */
  confirmLabel?: string;
  /** 承認処理中は主要ボタンを disable し、ラベルを「処理中…」にする。 */
  confirming?: boolean;
}

export default function PublishConfirmModal({
  open,
  onCancel,
  onConfirm,
  confirmLabel = '公開して投稿する',
  confirming = false,
}: PublishConfirmModalProps) {
  const firstCheckboxRef = useRef<HTMLInputElement>(null);
  // 権利者確認・規約同意の必須チェック。モーダルを開き直すたびにリセットする。
  const [rightsChecked, setRightsChecked] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onCancel]);

  useEffect(() => {
    if (!open) return;
    setRightsChecked(false);
    setTermsChecked(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    firstCheckboxRef.current?.focus();
  }, [open]);

  if (!open) return null;

  const canConfirm = rightsChecked && termsChecked;

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-labelledby="publish-confirm-title">
      <div className="modal__backdrop" onClick={onCancel} />
      <div className="modal__panel" onClick={(e) => e.stopPropagation()}>
        <div className="modal__header">
          <h3 id="publish-confirm-title" className="modal__title">
            公開してよろしいですか？
          </h3>
          <button type="button" className="modal__close" onClick={onCancel} aria-label="閉じる">
            ×
          </button>
        </div>
        <p className="modal__description">以下のことに同意した上で公開してください。</p>
        <ul className="modal__list">
          <li>公開すると、誰でも作品ページを閲覧でき、連携アプリで利用できるようになります。</li>
          <li>
            著作権や肖像権を侵害しない素材のみ公開できます（他人が権利を持つ映像・写真・音源・キャラクターなどを無断で使用しないでください）。
          </li>
          <li>購入した素材の再アップロードは、許諾がない限りできません。</li>
          <li>
            <Link to="/guidelines" target="_blank" rel="noopener noreferrer">
              コミュニティガイドライン
            </Link>
            に違反する内容は公開できません。
          </li>
          <li>公開後も、詳細ページから「非公開にする」でいつでも取り下げられます。</li>
        </ul>

        <div className="modal__checkboxes">
          <label className="radio">
            <input
              ref={firstCheckboxRef}
              type="checkbox"
              checked={rightsChecked}
              onChange={(e) => setRightsChecked(e.target.checked)}
            />
            私はこのコンテンツの権利者である、または権利者から再配布の許諾を得ています
          </label>
          <label className="radio">
            <input
              type="checkbox"
              checked={termsChecked}
              onChange={(e) => setTermsChecked(e.target.checked)}
            />
            <span>
              <Link to="/terms" target="_blank" rel="noopener noreferrer">
                利用規約
              </Link>
              と
              <Link to="/guidelines" target="_blank" rel="noopener noreferrer">
                コミュニティガイドライン
              </Link>
              に同意します
            </span>
          </label>
        </div>

        <div className="modal__actions">
          <button type="button" className="btn" onClick={onCancel}>
            キャンセル
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={onConfirm}
            disabled={confirming || !canConfirm}
          >
            {confirming ? '処理中…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
